import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getAllUsers } from '../redux/slices/userSlice'; // Adjust path to your user actions
import { fetchAllPackage } from '../redux/slices/packageSlice'; // Adjust path to your package actions
import axios from 'axios';

const CreateBookingPage = () => {
    const dispatch = useDispatch();

    // Redux state for users and tour packages
    const { usersList, loading: usersLoading, error: usersError } = useSelector((state) => state.user);
    const { allPackage, loading: packagesLoading, error: packagesError } = useSelector((state) => state.package);

    const [formData, setFormData] = useState({
        userId: '',
        tourPackageId: '',
        totalAmount: '',
        title: '',
        bookingDate: '',
        passengers: [{ firstName: '', lastName: '', email: '' }],
        status: 'pending',
    });
    const [billFile, setBillFile] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);

    // Fetch users and tour packages using Redux actions
    useEffect(() => {
        dispatch(getAllUsers());
        dispatch(fetchAllPackage());
    }, [dispatch]);

    // Handle form input changes
    const handleInputChange = (e, index) => {
        const { name, value } = e.target;
        if (index !== undefined) {
            const updatedPassengers = [...formData.passengers];
            updatedPassengers[index][name] = value;
            setFormData({ ...formData, passengers: updatedPassengers });
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    // Add new passenger field
    const addPassenger = () => {
        setFormData({
            ...formData,
            passengers: [...formData.passengers, { firstName: '', lastName: '', email: '' }],
        });
    };

    // Remove passenger field
    const removePassenger = (index) => {
        const updatedPassengers = formData.passengers.filter((_, i) => i !== index);
        setFormData({ ...formData, passengers: updatedPassengers });
    };

    // Handle bill file selection
    const handleFileChange = (e) => {
        setBillFile(e.target.files[0]);
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setLoading(true);

        try {
            const token = localStorage.getItem('token'); // Assuming token is stored in localStorage
            // Create booking
            const bookingResponse = await axios.post(
                'http://localhost:5500/api/bookings/phone',
                formData,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            // If bill file is provided, upload it
            if (billFile) {
                const formDataWithFile = new FormData();
                formDataWithFile.append('bill', billFile);
                await axios.put(
                    `http://localhost:5000/api/bookings/${bookingResponse.data.booking._id}/bill`,
                    formDataWithFile,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'multipart/form-data',
                        },
                    }
                );
            }

            setSuccess('Booking created successfully!');
            setFormData({
                userId: '',
                tourPackageId: '',
                totalAmount: '',
                title: '',
                bookingDate: '',
                passengers: [{ firstName: '', lastName: '', email: '' }],
                status: 'pending',
            });
            setBillFile(null);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to create booking');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-100 p-6">
            <div className="max-w-4xl mx-auto bg-white p-8 rounded-lg shadow-lg">
                <h1 className="text-2xl font-bold mb-6 text-gray-800">Create Phone Booking</h1>
                {(usersError || packagesError || error) && (
                    <p className="text-red-500 mb-4">{usersError || packagesError || error}</p>
                )}
                {success && <p className="text-green-500 mb-4">{success}</p>}
                {(usersLoading || packagesLoading) && (
                    <p className="text-blue-500 mb-4">Loading data...</p>
                )}
                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* User Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Select User</label>
                        <select
                            name="userId"
                            value={formData.userId}
                            onChange={handleInputChange}
                            className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                            required
                            disabled={usersLoading}
                        >
                            <option value="">Select a user</option>
                            {usersList.map((user) => (
                                <option key={user._id} value={user._id}>
                                    {user.name} ({user.email})
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Tour Package Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Select Tour Package</label>
                        <select
                            name="tourPackageId"
                            value={formData.tourPackageId}
                            onChange={handleInputChange}
                            className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                            required
                            disabled={packagesLoading}
                        >
                            <option value="">Select a tour package</option>
                            {allPackage.map((pkg) => (
                                <option key={pkg._id} value={pkg._id}>
                                    {pkg.title}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Booking Title */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Booking Title</label>
                        <input
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleInputChange}
                            className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                            required
                        />
                    </div>

                    {/* Booking Date */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Booking Date</label>
                        <input
                            type="date"
                            name="bookingDate"
                            value={formData.bookingDate}
                            onChange={handleInputChange}
                            className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                        />
                    </div>

                    {/* Total Amount */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Total Amount</label>
                        <input
                            type="text"
                            name="totalAmount"
                            value={formData.totalAmount}
                            onChange={handleInputChange}
                            className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                            required
                        />
                    </div>

                    {/* Passengers */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Passengers</label>
                        {formData.passengers.map((passenger, index) => (
                            <div key={index} className="mt-2 p-4 border border-gray-200 rounded-md">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <input
                                        type="text"
                                        name="firstName"
                                        value={passenger.firstName}
                                        onChange={(e) => handleInputChange(e, index)}
                                        placeholder="First Name"
                                        className="p-2 border border-gray-300 rounded-md"
                                        required
                                    />
                                    <input
                                        type="text"
                                        name="lastName"
                                        value={passenger.lastName}
                                        onChange={(e) => handleInputChange(e, index)}
                                        placeholder="Last Name"
                                        className="p-2 border border-gray-300 rounded-md"
                                        required
                                    />
                                    <input
                                        type="email"
                                        name="email"
                                        value={passenger.email}
                                        onChange={(e) => handleInputChange(e, index)}
                                        placeholder="Email"
                                        className="p-2 border border-gray-300 rounded-md"
                                        required
                                    />
                                </div>
                                {formData.passengers.length > 1 && (
                                    <button
                                        type="button"
                                        onClick={() => removePassenger(index)}
                                        className="mt-2 text-red-500 hover:text-red-700"
                                    >
                                        Remove Passenger
                                    </button>
                                )}
                            </div>
                        ))}
                        <button
                            type="button"
                            onClick={addPassenger}
                            className="mt-2 text-blue-500 hover:text-blue-700"
                        >
                            Add Passenger
                        </button>
                    </div>

                    {/* Bill Upload */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Upload Bill (Optional)</label>
                        <input
                            type="file"
                            onChange={handleFileChange}
                            accept=".pdf,.jpg,.jpeg,.png"
                            className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                        />
                    </div>

                    {/* Submit Button */}
                    <button
                        type="submit"
                        disabled={loading || usersLoading || packagesLoading}
                        className={`w-full p-2 text-white rounded-md ${loading || usersLoading || packagesLoading
                            ? 'bg-gray-400 cursor-not-allowed'
                            : 'bg-blue-600 hover:bg-blue-700'
                            }`}
                    >
                        {loading ? 'Creating Booking...' : 'Create Booking'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default CreateBookingPage;